import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PageHero from '../../components/feature/PageHero';
import ChatAssistant from '../../components/feature/ChatAssistant';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { trackFormSubmission, trackCTAClick } from '../../utils/leadTracking';
import { alertarSubmissaoFormulario } from '../../utils/emailAlerts';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-star-fill text-sm ${i < rating ? 'text-action-orange-500' : 'text-petrol-200'}`}></i>
        </div>
      ))}
    </div>
  );
}

function TestimonialContactCard({ testimonial, index, visible }: { testimonial: any; index: number; visible: boolean }) {
  return (
    <div
      className={`bg-white rounded-xl border border-petrol-100 shadow-md hover:shadow-xl transition-all duration-700 ease-out p-6 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Badge destaque */}
      <div className="inline-flex items-center gap-1.5 bg-action-orange-50 text-action-orange-600 text-xs font-bold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wide">
        <i className="ri-medal-line text-sm"></i>
        Caso em Destaque
      </div>

      {/* Header com foto */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 ring-3 ring-petrol-100 shadow-md bg-petrol-100">
          <img
            src={testimonial.foto}
            alt={testimonial.nome}
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
            width="56"
            height="56"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-petrol-900 text-sm">{testimonial.nome}</p>
          <p className="text-petrol-600 text-xs">{testimonial.cargo}</p>
          <p className="text-petrol-500 text-xs">{testimonial.empresa}</p>
          <div className="mt-1.5">
            <StarRating rating={testimonial.rating} />
          </div>
        </div>
      </div>

      {/* Depoimento */}
      <p className="text-petrol-700 text-sm leading-relaxed mb-4 italic line-clamp-4">
        "{testimonial.depoimento}"
      </p>

      {/* Resultado */}
      <div className="bg-gradient-to-r from-petrol-50 to-strategic-green-50 rounded-lg p-3 border border-petrol-100">
        <p className="text-xs text-petrol-500 font-semibold uppercase tracking-wide mb-0.5">Resultado</p>
        <p className="text-strategic-green-600 font-bold text-base">{testimonial.valorEconomizado}</p>
      </div>
    </div>
  );
}

type FieldErrors = {
  nome?: string;
  email?: string;
  telefone?: string;
  tipoSeguro?: string;
  mensagem?: string;
};

type TouchedFields = {
  nome?: boolean;
  email?: boolean;
  telefone?: boolean;
  tipoSeguro?: boolean;
  mensagem?: boolean;
};

function validateField(name: string, value: string): string {
  switch (name) {
    case 'nome':
      if (!value.trim()) return 'Nome é obrigatório.';
      if (value.trim().length < 3) return 'Nome deve ter ao menos 3 caracteres.';
      return '';
    case 'email':
      if (!value.trim()) return 'E-mail é obrigatório.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Informe um e-mail válido.';
      return '';
    case 'telefone':
      if (!value.trim()) return 'Telefone é obrigatório.';
      if (value.replace(/\D/g, '').length < 10) return 'Informe um telefone válido com DDD.';
      return '';
    case 'tipoSeguro':
      if (!value) return 'Selecione o tipo de seguro.';
      return '';
    case 'mensagem':
      if (!value.trim()) return 'Mensagem é obrigatória.';
      if (value.trim().length < 10) return 'Mensagem deve ter ao menos 10 caracteres.';
      if (value.length > 500) return 'Mensagem não pode ultrapassar 500 caracteres.';
      return '';
    default:
      return '';
  }
}

export default function ContatoPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    tipoSeguro: '',
    mensagem: ''
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation();

  const featuredTestimonials = [
    {
      id: 1,
      nome: 'Carlos Eduardo Mendes',
      cargo: 'CEO',
      empresa: 'Mendes Transportes Ltda',
      foto: 'https://readdy.ai/api/search-image?query=professional%20brazilian%20male%20CEO%20transportation%20company%20confident%20warm%20smile%20portrait%20clean%20studio%20background%20corporate%20headshot%20natural%20lighting%20distinguished%20executive&width=400&height=400&seq=ibxdep-001&orientation=squarish',
      depoimento: 'Antes de conhecer a IBX Brasil, renovava meus seguros sem questionar nada. Achava que era assim mesmo: você paga o que a seguradora cobra e pronto. Depois da análise deles, percebi que estava pagando por coberturas que não precisava e, pior, sem proteção em áreas críticas para o meu negócio. A equipe fez um trabalho minucioso, explicou cada ponto da apólice e negociou condições que eu jamais conseguiria sozinho.',
      valorEconomizado: 'R$ 87.000/ano',
      rating: 5,
    },
    {
      id: 2,
      nome: 'Fernanda Leal',
      cargo: 'Diretora Financeira',
      empresa: 'Construtora Leal & Associados',
      foto: 'https://readdy.ai/api/search-image?query=professional%20brazilian%20businesswoman%20financial%20director%20confident%20elegant%20smile%20portrait%20clean%20studio%20background%20corporate%20headshot%20natural%20lighting%20sophisticated%20executive&width=400&height=400&seq=ibxdep-002&orientation=squarish',
      depoimento: 'A gestão dos nossos contratos de seguros era um caos. Tínhamos apólices contratadas em momentos diferentes, com seguradoras diferentes, vencimentos desencontrados e nenhum controle real. A IBX Brasil organizou tudo, renegociou com as seguradoras e hoje temos controle claro de coberturas, vencimentos e custos. A tranquilidade que isso trouxe para a diretoria não tem preço.',
      valorEconomizado: 'R$ 62.000/ano',
      rating: 5,
    },
    {
      id: 3,
      nome: 'Carlos Eduardo Silva',
      cargo: 'Diretor Executivo',
      empresa: 'Indústria de Alimentos Silva',
      foto: 'https://readdy.ai/api/search-image?query=professional%20brazilian%20male%20executive%20director%20food%20industry%20confident%20serious%20portrait%20clean%20studio%20background%20corporate%20headshot%20natural%20lighting%20authoritative&width=400&height=400&seq=ibxdep-003&orientation=squarish',
      depoimento: 'Tivemos um sinistro de R$ 340 mil negado pela seguradora sob alegação de falta de manutenção preventiva. Estávamos prestes a arcar com o prejuízo integral quando contratamos a IBX Brasil. A equipe analisou toda a documentação, identificou cláusulas contratuais favoráveis e conduziu a negociação de forma técnica e assertiva. Em 32 dias, o sinistro foi integralmente aprovado. Sem o trabalho deles, teríamos perdido tudo.',
      valorEconomizado: 'R$ 340.000 recuperados',
      rating: 5,
    },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name as keyof TouchedFields]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const getFieldState = (name: keyof FieldErrors) => {
    if (!touched[name]) return 'idle';
    return errors[name] ? 'error' : 'success';
  };

  const getInputClass = (name: keyof FieldErrors) => {
    const state = getFieldState(name);
    const base = 'w-full px-4 py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none transition-all text-sm';
    if (state === 'error') return `${base} border-red-400 focus:ring-red-300 bg-red-50`;
    if (state === 'success') return `${base} border-green-400 focus:ring-green-300 bg-green-50/30`;
    return `${base} border-gray-300 focus:ring-orange-400`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validar todos os campos obrigatórios
    const requiredFields: (keyof FieldErrors)[] = ['nome', 'email', 'telefone', 'tipoSeguro', 'mensagem'];
    const newErrors: FieldErrors = {};
    const newTouched: TouchedFields = {};
    requiredFields.forEach(field => {
      newTouched[field] = true;
      const err = validateField(field, formData[field as keyof typeof formData]);
      if (err) newErrors[field] = err;
    });
    setTouched(newTouched);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6j1ipmtn3rcgj1rbbf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        await alertarSubmissaoFormulario(
          {
            nome: formData.nome,
            email: formData.email,
            telefone: formData.telefone,
            empresa: formData.empresa,
            interesse: formData.tipoSeguro,
            mensagem: formData.mensagem
          },
          'Formulário de Contato - Site'
        );

        trackFormSubmission(
          {
            nome: formData.nome,
            email: formData.email,
            telefone: formData.telefone,
            empresa: formData.empresa,
            servicoInteresse: formData.tipoSeguro,
            mensagem: formData.mensagem
          },
          'Contato',
          'Enviar Solicitação'
        );

        setSubmitStatus('success');
        setFormData({ nome: '', email: '', telefone: '', empresa: '', tipoSeguro: '', mensagem: '' });
        setTouched({});
        setErrors({});
        setTimeout(() => { navigate('/contato/obrigado'); }, 1000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = [
    {
      question: 'Quanto tempo leva para receber um retorno após o contato?',
      answer: 'Nossa equipe responde em até 24 horas úteis para mensagens enviadas pelo formulário ou e-mail. Para atendimento imediato, recomendamos o WhatsApp — geralmente respondemos em minutos durante o horário comercial.'
    },
    {
      question: 'A análise gratuita tem algum compromisso de contratação?',
      answer: 'Não. A análise gratuita é totalmente sem compromisso. Após apresentarmos o diagnóstico e as recomendações, você decide livremente se deseja contratar nossos serviços. Nossa confiança está na qualidade do trabalho que entregamos.'
    },
    {
      question: 'Vocês atendem clientes fora de Vitória da Conquista?',
      answer: 'Sim! Atendemos clientes em todo o Brasil. Graças ao atendimento remoto por WhatsApp, e-mail e videoconferência, oferecemos o mesmo nível de qualidade independentemente da sua localização.'
    },
    {
      question: 'Quais informações devo ter em mãos para a primeira consulta?',
      answer: 'Para a análise inicial, é útil ter: apólices de seguros vigentes (se houver), informações sobre veículos ou patrimônio a proteger, número de colaboradores (para seguros empresariais) e uma ideia do orçamento disponível. Mas não se preocupe — nossa equipe guia você em todo o processo.'
    },
    {
      question: 'A IBX Brasil cobra alguma taxa de consultoria?',
      answer: 'Nossa remuneração é baseada em comissão paga pelas seguradoras, o que significa que você não paga nada a mais pelo nosso serviço de consultoria. Você paga apenas o prêmio do seguro, e trabalhamos para que esse valor seja o mais competitivo possível.'
    },
    {
      question: 'Posso solicitar ajuda com um sinistro que já está em andamento?',
      answer: 'Sim. Oferecemos suporte na recuperação de sinistros mesmo para apólices contratadas em outras corretoras. Entre em contato descrevendo a situação e nossa equipe avaliará como podemos ajudar.'
    }
  ];

  function FAQContactItem({ item, index }: { item: { question: string; answer: string }; index: number }) {
    const [open, setOpen] = useState(false);
    return (
      <div className={`border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 ${open ? 'shadow-md' : 'hover:shadow-sm'}`}>
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer min-h-[56px]"
          aria-expanded={open}
          aria-controls={`faq-contact-answer-${index}`}
          id={`faq-contact-question-${index}`}
          style={{ transitionDelay: `${index * 50}ms` }}
        >
          <span className="text-sm sm:text-base font-semibold text-petrol-900 leading-snug">{item.question}</span>
          <span className={`w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${open ? 'bg-action-orange-500 text-white rotate-45' : 'bg-petrol-50 text-petrol-600'}`}>
            <i className="ri-add-line text-lg" aria-hidden="true"></i>
          </span>
        </button>
        <div
          id={`faq-contact-answer-${index}`}
          role="region"
          aria-labelledby={`faq-contact-question-${index}`}
          className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className="px-6 pb-6 pt-1 bg-white border-t border-gray-100">
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.answer}</p>
          </div>
        </div>
      </div>
    );
  }

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Entre em Contato - IBX Brasil",
    "description": "Fale com nossos especialistas em seguros empresariais. Agende uma análise gratuita das suas apólices.",
    "url": "https://ibxbrasil.com.br/contato"
  };

  return (
    <>
      <SEOHead
        title="Entre em Contato - Consultoria em Seguros | IBX Brasil"
        description="Fale com nossos especialistas em seguros empresariais. Agende uma análise gratuita das suas apólices e descubra como proteger melhor sua empresa."
        canonical="https://ibxbrasil.com.br/contato"
        ogImage="https://readdy.ai/api/search-image?query=professional%20insurance%20consultant%20ready%20to%20help%20clients%20with%20contact%20form%20and%20communication%20channels%20in%20modern%20office&width=1200&height=630&seq=ibx-contato-og&orientation=landscape"
        schema={contactPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <PageHero
          badge="FALE CONOSCO"
          title="Entre em **Contato** com a IBX Brasil"
          subtitle="Estamos prontos para analisar suas necessidades e apresentar soluções personalizadas em gestão de riscos e contratação de seguros."
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20customer%20service%20contact%20center%20modern%20office%20interior%20insurance%20consulting%20elegant%20workspace%20deep%20teal%20petrol%20blue%20accents%20natural%20lighting%20sophisticated%20corporate%20environment%20communication&width=1920&height=1080&seq=ibxcontato-hero&orientation=landscape"
          primaryCta={{
            text: "Solicitar Análise Gratuita",
            action: () => {
              document.getElementById('contato-form')?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          secondaryCta={{
            text: "Falar no WhatsApp",
            href: "https://wa.me/5577988080505",
            onClick: () => trackCTAClick('Hero - Falar no WhatsApp', 'Contato Geral', 'https://wa.me/5577988080505')
          }}
          overlayIntensity="medium"
        />

        <main id="main-content">
          {/* Contact Content */}
          <section className="py-16 sm:py-20" aria-labelledby="contact-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                
                {/* Contact Information */}
                <div>
                  <h2 id="contact-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
                    Informações de Contato
                  </h2>
                  
                  <div className="space-y-5 sm:space-y-6 mb-8 sm:mb-10">
                    {/* WhatsApp */}
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <i className="ri-whatsapp-line text-2xl text-orange-600"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">WhatsApp</h3>
                        <a 
                          href="https://wa.me/5577988080505" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={() => trackCTAClick('Contato - WhatsApp Direto', 'Contato Geral', 'https://wa.me/5577988080505')}
                          className="text-orange-600 hover:text-orange-700 font-medium cursor-pointer break-all"
                          aria-label="Ligar para (77) 98808-0505 via WhatsApp"
                        >
                          (77) 98808-0505
                        </a>
                        <p className="text-sm text-gray-600 mt-1">Atendimento rápido e direto</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <i className="ri-mail-line text-2xl text-orange-600"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">E-mail</h3>
                        <a 
                          href="mailto:contato@ibxbrasil.com"
                          className="text-orange-600 hover:text-orange-700 font-medium cursor-pointer break-all"
                          aria-label="Enviar email para contato@ibxbrasil.com"
                        >
                          contato@ibxbrasil.com
                        </a>
                        <p className="text-sm text-gray-600 mt-1">Resposta em até 24 horas</p>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <i className="ri-map-pin-line text-2xl text-orange-600"></i>
                      </div>
                      <address className="min-w-0 flex-1 not-italic">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Endereço</h3>
                        <p className="text-sm sm:text-base text-gray-700">
                          Rua Pastor Arthur de Souza Freire, 360<br />
                          Vitória da Conquista/BA
                        </p>
                      </address>
                    </div>

                    {/* Business Hours */}
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <i className="ri-time-line text-2xl text-orange-600"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Horário de Atendimento</h3>
                        <p className="text-sm sm:text-base text-gray-700">
                          Segunda a Sexta: 8h às 18h<br />
                          Sábado: 8h às 12h
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <div className="rounded-lg overflow-hidden shadow-lg h-64 sm:h-80">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3867.8!2d-40.8394!3d-14.8615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTTCsDUxJzQxLjQiUyA0MMKwNTAnMjEuOCJX!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localização da IBX Brasil em Vitória da Conquista"
                      aria-label="Mapa mostrando a localização da IBX Brasil"
                    ></iframe>
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-sm">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                      Solicite uma Análise Gratuita
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                      Preencha o formulário e nossa equipe entrará em contato para entender suas necessidades.
                    </p>

                    <form 
                      id="contato-form" 
                      onSubmit={handleSubmit} 
                      data-readdy-form
                      aria-label="Formulário de contato"
                      noValidate
                    >
                      <div className="space-y-4 sm:space-y-5">

                        {/* Nome */}
                        <div>
                          <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                            Nome Completo <span className="text-red-600" aria-label="obrigatório">*</span>
                          </label>
                          <input
                            type="text"
                            id="nome"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={getFieldState('nome') === 'error' ? 'true' : getFieldState('nome') === 'success' ? 'false' : undefined}
                            aria-describedby={errors.nome ? 'nome-error' : undefined}
                            className={getInputClass('nome')}
                            placeholder="Seu nome completo"
                            autoComplete="name"
                          />
                          {touched.nome && errors.nome && (
                            <p id="nome-error" className="field-error" role="alert">
                              <i className="ri-error-warning-line" aria-hidden="true"></i>
                              {errors.nome}
                            </p>
                          )}
                          {touched.nome && !errors.nome && (
                            <p className="field-success">
                              <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                              Nome válido
                            </p>
                          )}
                        </div>

                        {/* Email */}
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            E-mail <span className="text-red-600" aria-label="obrigatório">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={getFieldState('email') === 'error' ? 'true' : getFieldState('email') === 'success' ? 'false' : undefined}
                            aria-describedby={errors.email ? 'email-error' : undefined}
                            className={getInputClass('email')}
                            placeholder="seu@email.com"
                            autoComplete="email"
                          />
                          {touched.email && errors.email && (
                            <p id="email-error" className="field-error" role="alert">
                              <i className="ri-error-warning-line" aria-hidden="true"></i>
                              {errors.email}
                            </p>
                          )}
                          {touched.email && !errors.email && (
                            <p className="field-success">
                              <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                              E-mail válido
                            </p>
                          )}
                        </div>

                        {/* Telefone */}
                        <div>
                          <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                            Telefone/WhatsApp <span className="text-red-600" aria-label="obrigatório">*</span>
                          </label>
                          <input
                            type="tel"
                            id="telefone"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={getFieldState('telefone') === 'error' ? 'true' : getFieldState('telefone') === 'success' ? 'false' : undefined}
                            aria-describedby={errors.telefone ? 'telefone-error' : undefined}
                            className={getInputClass('telefone')}
                            placeholder="(00) 00000-0000"
                            autoComplete="tel"
                          />
                          {touched.telefone && errors.telefone && (
                            <p id="telefone-error" className="field-error" role="alert">
                              <i className="ri-error-warning-line" aria-hidden="true"></i>
                              {errors.telefone}
                            </p>
                          )}
                          {touched.telefone && !errors.telefone && (
                            <p className="field-success">
                              <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                              Telefone válido
                            </p>
                          )}
                        </div>

                        {/* Empresa */}
                        <div>
                          <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                            Empresa <span className="text-gray-500 text-xs">(opcional)</span>
                          </label>
                          <input
                            type="text"
                            id="empresa"
                            name="empresa"
                            value={formData.empresa}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all text-sm"
                            placeholder="Nome da empresa"
                            autoComplete="organization"
                          />
                        </div>

                        {/* Tipo de Seguro */}
                        <div>
                          <label htmlFor="tipoSeguro" className="block text-sm font-medium text-gray-700 mb-2">
                            Tipo de Seguro <span className="text-red-600" aria-label="obrigatório">*</span>
                          </label>
                          <select
                            id="tipoSeguro"
                            name="tipoSeguro"
                            value={formData.tipoSeguro}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={getFieldState('tipoSeguro') === 'error' ? 'true' : getFieldState('tipoSeguro') === 'success' ? 'false' : undefined}
                            aria-describedby={errors.tipoSeguro ? 'tipoSeguro-error' : undefined}
                            className={`${getInputClass('tipoSeguro')} bg-white cursor-pointer`}
                          >
                            <option value="">Selecione o tipo de seguro</option>
                            <option value="Seguro Automóvel">Seguro Automóvel</option>
                            <option value="Vida Empresarial">Vida Empresarial</option>
                            <option value="Gestão de Contratos">Gestão de Contratos</option>
                            <option value="Recuperação de Sinistros">Recuperação de Sinistros</option>
                            <option value="Consultoria Técnica">Consultoria Técnica</option>
                            <option value="Suporte e Acompanhamento">Suporte e Acompanhamento</option>
                            <option value="Outro">Outro</option>
                          </select>
                          {touched.tipoSeguro && errors.tipoSeguro && (
                            <p id="tipoSeguro-error" className="field-error" role="alert">
                              <i className="ri-error-warning-line" aria-hidden="true"></i>
                              {errors.tipoSeguro}
                            </p>
                          )}
                        </div>

                        {/* Mensagem */}
                        <div>
                          <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                            Mensagem <span className="text-red-600" aria-label="obrigatório">*</span>
                          </label>
                          <textarea
                            id="mensagem"
                            name="mensagem"
                            value={formData.mensagem}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={getFieldState('mensagem') === 'error' ? 'true' : getFieldState('mensagem') === 'success' ? 'false' : undefined}
                            aria-describedby="mensagem-help mensagem-count"
                            rows={4}
                            maxLength={500}
                            className={`${getInputClass('mensagem')} resize-none`}
                            placeholder="Descreva suas necessidades ou dúvidas..."
                          ></textarea>
                          <div className="flex items-center justify-between mt-1">
                            <p id="mensagem-help" className="text-xs text-gray-500">Máximo de 500 caracteres</p>
                            <p id="mensagem-count" className={`text-xs font-medium ${formData.mensagem.length > 450 ? 'text-red-500' : 'text-gray-400'}`}>
                              {formData.mensagem.length}/500
                            </p>
                          </div>
                          {touched.mensagem && errors.mensagem && (
                            <p className="field-error" role="alert">
                              <i className="ri-error-warning-line" aria-hidden="true"></i>
                              {errors.mensagem}
                            </p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isSubmitting ? 'Enviando...' : 'Solicitar Análise Gratuita'}
                        </button>

                        {submitStatus === 'success' && (
                          <div 
                            className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2"
                            role="alert"
                            aria-live="polite"
                          >
                            <i className="ri-checkbox-circle-line text-green-600 text-lg flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                            <div>
                              <strong>Mensagem enviada com sucesso!</strong> Nossa equipe entrará em contato em breve.
                            </div>
                          </div>
                        )}

                        {submitStatus === 'error' && (
                          <div 
                            className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2"
                            role="alert"
                            aria-live="assertive"
                          >
                            <i className="ri-error-warning-line text-red-600 text-lg flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                            <div>
                              <strong>Erro ao enviar mensagem.</strong> Por favor, tente novamente ou entre em contato via WhatsApp.
                            </div>
                          </div>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="mt-6 sm:mt-8 bg-orange-50 rounded-xl p-5 sm:p-6 border border-orange-100">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">
                      Prefere falar diretamente?
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 sm:mb-4">
                      Entre em contato pelo WhatsApp para atendimento imediato.
                    </p>
                    <a
                      href="https://wa.me/5577988080505"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackCTAClick('Contato - Quick WhatsApp', 'Contato Geral', 'https://wa.me/5577988080505')}
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-5 sm:px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap text-sm cursor-pointer min-h-[48px]"
                      aria-label="Falar conosco pelo WhatsApp agora"
                    >
                      <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i>
                      Falar no WhatsApp
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section className="py-20 bg-gradient-to-b from-white to-petrol-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-action-orange-100 text-action-orange-600 text-xs font-bold px-4 py-2 rounded-full mb-4 uppercase tracking-widest">
                  <i className="ri-chat-quote-line" aria-hidden="true"></i>
                  Depoimentos Reais
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-petrol-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Quem Confiou na <span className="text-action-orange-500">IBX Brasil</span>
                </h2>
                <p className="text-petrol-600 text-base max-w-2xl mx-auto">
                  Veja o que nossos clientes têm a dizer sobre os resultados obtidos com nossa consultoria especializada.
                </p>
              </div>

              <div
                ref={testimonialsRef as React.RefObject<HTMLDivElement>}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10"
              >
                {featuredTestimonials.map((testimonial, index) => (
                  <TestimonialContactCard
                    key={testimonial.id}
                    testimonial={testimonial}
                    index={index}
                    visible={testimonialsVisible}
                  />
                ))}
              </div>

              <div className="text-center">
                <Link
                  to="/depoimentos"
                  className="inline-flex items-center justify-center gap-2 bg-petrol-800 hover:bg-petrol-900 text-white px-8 py-4 rounded-xl font-bold text-base transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-chat-3-line text-xl" aria-hidden="true"></i>
                  Ver Todos os Depoimentos
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-gray-50" aria-labelledby="faq-contato-heading">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <span className="inline-block bg-petrol-50 text-petrol-700 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                  DÚVIDAS FREQUENTES
                </span>
                <h2 id="faq-contato-heading" className="text-3xl sm:text-4xl font-bold text-petrol-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Perguntas Frequentes
                </h2>
                <p className="text-gray-600 text-sm sm:text-base max-w-xl mx-auto">
                  Respondemos as dúvidas mais comuns de quem está entrando em contato com a IBX Brasil pela primeira vez.
                </p>
                <div className="h-1 w-16 bg-action-orange-500 rounded-full mx-auto mt-6"></div>
              </div>

              <div className="space-y-3">
                {faqItems.map((item, i) => (
                  <FAQContactItem key={i} item={item} index={i} />
                ))}
              </div>

              <div className="mt-10 text-center">
                <p className="text-sm text-gray-500 mb-4">Não encontrou o que procurava?</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/faq"
                    className="inline-flex items-center justify-center gap-2 bg-petrol-800 hover:bg-petrol-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-petrol-900 transition-colors text-sm whitespace-nowrap cursor-pointer min-h-[48px]"
                  >
                    <i className="ri-question-answer-line text-lg" aria-hidden="true"></i>
                    Ver Central de Dúvidas Completa
                  </Link>
                  <a
                    href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20tenho%20uma%20d%C3%BAvida%20e%20gostaria%20de%20falar%20com%20a%20equipe%20IBX%20Brasil."
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-strategic-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-strategic-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer min-h-[48px]"
                    aria-label="Perguntar diretamente no WhatsApp"
                  >
                    <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                    Perguntar no WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-700">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Pronto para Profissionalizar sua Contratação de Seguros?
              </h2>
              <p className="text-lg text-orange-50 mb-8">
                Agende uma análise gratuita e descubra como podemos reduzir seus custos e aumentar sua proteção.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/5577988080505"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCTAClick('Contato CTA - WhatsApp', 'Contato Geral', 'https://wa.me/5577988080505')}
                  className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer min-h-[52px]"
                  aria-label="Falar no WhatsApp para análise gratuita"
                >
                  <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i>
                  Falar no WhatsApp
                </a>
                <a
                  href="mailto:contato@ibxbrasil.com"
                  className="inline-flex items-center justify-center gap-2 bg-orange-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-900 transition-colors whitespace-nowrap cursor-pointer min-h-[52px]"
                  aria-label="Enviar e-mail para contato@ibxbrasil.com"
                >
                  <i className="ri-mail-line text-xl" aria-hidden="true"></i>
                  Enviar E-mail
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}